import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Button } from '@/components'
import { EmptyView } from '@/components/EmptyView.tsx'
import { useUnifiedContext } from '@/contexts/UnifiedContext'
import { Deployment } from '@/models/deployment.ts'
import { getCredentialStatus, isCredentialExpired } from '@/utils/credential'

interface Props {
  _deployment: Deployment
}

export default function DeploymentCredential({ _deployment }: Props) {
  const { t } = useTranslation('autoDeploy')
  const { mode } = useUnifiedContext()

  const accessKeysPath = mode === 'team' ? '/team/profile/access-keys' : '/profile/access-keys'
  const credentials = _deployment.credentials

  return (
    <div className="flex flex-col gap-4">
      {credentials.length > 0 ? (
        <div className="w-full">
          {/* Table Header */}
          <div className="flex w-full">
            <div className="flex-1 px-4 py-3">
              <span className="t-sb text-secondary text-sm">{t('credential.name')}</span>
            </div>
            <div className="flex-[2] px-4 py-3 min-w-0">
              <span className="t-sb text-secondary text-sm">{t('credential.accessKey')}</span>
            </div>
            <div className="flex-1 px-4 py-3">
              <span className="t-sb text-secondary text-sm">{t('credential.expiresAt')}</span>
            </div>
            <div className="w-[100px] px-4 py-3">
              <span className="t-sb text-secondary text-sm">{t('credential.status')}</span>
            </div>
          </div>

          {/* Table Body */}
          <div className="rounded-lg overflow-hidden">
            {credentials.map((cred, index) => {
              const expiresAt = dayjs.unix(cred.expiresAt?.seconds || 0)
              const isExpired = isCredentialExpired(cred)
              const isLast = index === credentials.length - 1

              return (
                <div
                  key={cred.resID}
                  className={`flex w-full bg-panel-white h-[44px] items-center ${!isLast ? 'border-b border-neutral-100' : ''}`}
                >
                  <div className="flex-1 px-4">
                    <span className="t-sb text-primary text-sm">{cred.title}</span>
                  </div>
                  <div className="flex-[2] px-4 min-w-0">
                    <span className="t-sb text-primary text-sm block truncate">
                      {cred.accessKey}
                    </span>
                  </div>
                  <div className="flex-1 px-4">
                    <span
                      className={`t-sb text-sm ${isExpired ? 'text-[#fb3748]' : 'text-primary'}`}
                    >
                      {cred.expiresAt?.seconds ? expiresAt.format('YYYY-MM-DD HH:mm:ss') : '-'}
                    </span>
                  </div>
                  <div className="w-[100px] px-4">
                    <span
                      className={`t-sb text-sm ${isExpired ? 'text-error' : cred.enabled ? 'text-active' : 'text-tertiary'}`}
                      title={isExpired ? t('credential.expiredHint') : undefined}
                    >
                      {t(`credential.${getCredentialStatus(cred)}`)}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ) : (
        <EmptyView
          imageSize="small"
          title={<span className="t-sm text-secondary">{t('credential.noCredentialBound')}</span>}
          action={
            <Link to={accessKeysPath}>
              <Button type="secondary" size="sm">
                {t('credential.bindCredential')}
              </Button>
            </Link>
          }
        />
      )}

      {credentials.length > 0 && (
        <Link to={accessKeysPath} className="text-highlight t-x-sb hover:underline">
          {t('credential.manageLink')}
        </Link>
      )}
    </div>
  )
}
